import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "../../../assets/svg/search-icon";
import CheckIcon from "../../../assets/svg/check-icon";
import Button from "../../Button/Button";
import { RootState } from "../../../redux/store";
import {
  setCommunicationsAttributionChannel,
  setCommunicationsStandardMedium,
  setCommunicationsStandardSource,
  setCommunicationsDeviceType,
  setCrmLeadsAttributionChannel,
  setCrmLeadsStandardMedium,
  setCrmLeadsStandardSource,
  setCrmLeadsDeviceType,
} from "../../../redux/slices/filterSlice";
import {
  useGetCrmDistinctValuesQuery,
  CrmAttributionField,
} from "../../../redux/services/crmApi";

const SLICE_KEY_BY_FIELD = {
  attribution_channel: "attributionChannel",
  standard_medium: "standardMedium",
  standard_source: "standardSource",
  device_type: "deviceType",
} as const;

const SET_ACTION_BY_FIELD = {
  attribution_channel: {
    crm: setCrmLeadsAttributionChannel,
    communications: setCommunicationsAttributionChannel,
  },
  standard_medium: {
    crm: setCrmLeadsStandardMedium,
    communications: setCommunicationsStandardMedium,
  },
  standard_source: {
    crm: setCrmLeadsStandardSource,
    communications: setCommunicationsStandardSource,
  },
  device_type: {
    crm: setCrmLeadsDeviceType,
    communications: setCommunicationsDeviceType,
  },
} as const;

const LABEL_BY_FIELD: Record<CrmAttributionField, string> = {
  attribution_channel: "Attribution Channels",
  standard_medium: "Standard Mediums",
  standard_source: "Standard Sources",
  device_type: "Device Types",
};

const AttributionFilter = ({
  field,
  onClose,
  isCrm = false,
}: {
  field: CrmAttributionField;
  onClose: () => void;
  isCrm?: boolean;
}) => {
  const dispatch = useDispatch();
  const appliedFilter = useSelector((state: RootState) =>
    isCrm
      ? state.filter.crmLeads[SLICE_KEY_BY_FIELD[field]]
      : state.filter.communications[SLICE_KEY_BY_FIELD[field]]
  );
  const { data, isLoading, isFetching } = useGetCrmDistinctValuesQuery(field, {
    refetchOnMountOrArgChange: true,
  });
  const [selectedValues, setSelectedValues] = useState<string[]>(
    appliedFilter.value
  );
  const [searchQuery, setSearchQuery] = useState("");

  const label = LABEL_BY_FIELD[field];
  const options: string[] = data?.values ?? [];
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = (option: string) => {
    setSelectedValues((prev) =>
      prev.includes(option)
        ? prev.filter((value) => value !== option)
        : [...prev, option]
    );
  };

  const handleApply = () => {
    dispatch(
      SET_ACTION_BY_FIELD[field][isCrm ? "crm" : "communications"]({
        value: selectedValues,
        displayValue: selectedValues,
      })
    );
    onClose();
  };

  const isApplyDisabled =
    JSON.stringify([...selectedValues].sort()) ===
    JSON.stringify([...appliedFilter.value].sort());

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-70px)]">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search ${label}`}
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm px-3 py-1">
        SELECTED ({selectedValues?.length ?? 0})
      </span>

      <div className="overflow-y-auto">
        {/* Selected Values Display */}
        {selectedValues.length > 0 && (
          <div className="flex flex-col divide-y divide-border-gray dark:divide-border-gray-dk">
            {selectedValues.map((option) => (
              <div
                key={option}
                onClick={() => handleToggle(option)}
                className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                    {option}
                  </span>
                </div>
                <div className="flex items-center justify-center w-3 h-3 border rounded border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk">
                  <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Options List */}
        <div className="flex flex-col">
          <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
            All {label}
          </div>

          <div className="overflow-y-auto">
            {isLoading || isFetching ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {isLoading
                    ? `Loading ${label.toLowerCase()}...`
                    : `Fetching ${label.toLowerCase()}...`}
                </div>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {searchQuery.trim()
                    ? `No ${label.toLowerCase()} found`
                    : `No ${label.toLowerCase()} available`}
                </div>
              </div>
            ) : (
              filteredOptions
                .filter((option) => !selectedValues.includes(option))
                .map((option) => (
                  <div
                    key={option}
                    onClick={() => handleToggle(option)}
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                        {option}
                      </div>
                    </div>
                    <div className="flex items-center justify-center w-3 h-3 border rounded border-border-gray dark:border-border-gray-dk">
                      <div className="w-3 h-3 rounded-sm bg-transparent" />
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-6 px-3">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={isApplyDisabled}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default AttributionFilter;
